import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CalendarCheck, Clock, User, CheckCircle, ArrowRight, ArrowLeft, CaretRight } from '@phosphor-icons/react';
import SeoHead from '../../components/SeoHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORE_SLUG = 'mrg';

const fmtDay = (d) => d.toLocaleDateString('fr-FR', { weekday: 'short' });
const fmtNum = (d) => d.getDate();
const fmtMon = (d) => d.toLocaleDateString('fr-FR', { month: 'short' });

const RendezVousPage = () => {
  const [store, setStore] = useState(null);
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [practitioner, setPractitioner] = useState('indifferent');
  const [selDate, setSelDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selTime, setSelTime] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API_URL}/api/s/${STORE_SLUG}`).then(r => setStore(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/s/${STORE_SLUG}/rdv/config`).then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const colors = useMemo(() => {
    const id = store?.identity || {};
    return { primary: id.primary_color || '#111111', accent: id.accent_color || '#FF004D' };
  }, [store]);
  const accent = colors.accent;

  // next 21 days excluding Sundays
  const days = useMemo(() => {
    const out = [];
    const d0 = new Date(); d0.setHours(0, 0, 0, 0);
    for (let i = 0; i < 28 && out.length < 21; i++) {
      const d = new Date(d0); d.setDate(d0.getDate() + i);
      if (d.getDay() !== 0) out.push(d);
    }
    return out;
  }, []);

  const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const pickDate = (d) => {
    setSelDate(d); setSelTime(null); setLoadingSlots(true);
    axios.get(`${API_URL}/api/s/${STORE_SLUG}/rdv/slots?date=${isoDate(d)}`)
      .then(r => setSlots(r.data?.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  };

  const submit = async () => {
    setError('');
    if (!form.name.trim() || !form.phone.trim()) { setError('Merci d’indiquer votre nom et votre téléphone.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/s/${STORE_SLUG}/rdv/book`, {
        service: service.id, practitioner, date: isoDate(selDate), time: selTime,
        name: form.name, phone: form.phone, email: form.email, notes: form.notes,
      });
      setConfirmed(res.data.appointment);
      window.scrollTo(0, 0);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Une erreur est survenue. Réessayez.');
      if (e?.response?.status === 409 && selDate) pickDate(selDate);
    } finally {
      setSubmitting(false);
    }
  };

  const Step = ({ n, label }) => (
    <div className="flex items-center gap-2">
      <span className="w-7 h-7 rounded-full flex items-center justify-center text-[13px] font-bold text-white transition-colors"
        style={{ backgroundColor: step >= n ? accent : '#cbd5e1' }}>{step > n ? '✓' : n}</span>
      <span className={`text-[13px] font-medium hidden sm:inline ${step >= n ? 'text-slate-900' : 'text-slate-400'}`}>{label}</span>
    </div>
  );

  if (confirmed) {
    return (
      <div className="bg-[#f6f7f9] min-h-screen py-16" data-testid="rdv-confirmed">
        <SeoHead title="Rendez-vous confirmé | MRG Optique & Audition" description="Votre rendez-vous chez MRG Optique est confirmé." />
        <div className="max-w-xl mx-auto px-6 text-center bg-white rounded-3xl shadow-sm border border-slate-100 py-12">
          <CheckCircle size={64} weight="fill" className="mx-auto mb-4" style={{ color: accent }} />
          <h1 className="text-[26px] font-semibold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>Rendez-vous confirmé&nbsp;!</h1>
          <p className="text-slate-500 mt-2">Nous vous attendons en boutique. Un membre de l’équipe vous recontactera si besoin.</p>
          <div className="mt-6 text-left bg-slate-50 rounded-2xl p-5 space-y-2 text-[14px]">
            <div className="flex justify-between"><span className="text-slate-500">Prestation</span><strong className="text-slate-800">{confirmed.service_name}</strong></div>
            <div className="flex justify-between"><span className="text-slate-500">Praticien</span><strong className="text-slate-800">{confirmed.practitioner_name}</strong></div>
            <div className="flex justify-between"><span className="text-slate-500">Date</span><strong className="text-slate-800">{new Date(confirmed.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</strong></div>
            <div className="flex justify-between"><span className="text-slate-500">Heure</span><strong className="text-slate-800">{confirmed.time}</strong></div>
          </div>
          <a href="/" className="inline-block mt-7 px-7 py-3.5 rounded-full text-white font-semibold" style={{ backgroundColor: colors.primary }}>Retour à l’accueil</a>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f6f7f9] min-h-screen" data-testid="rdv-page">
      <SeoHead title="Prendre rendez-vous | MRG Optique & Audition Istres"
        description="Prenez rendez-vous en ligne avec votre opticien MRG à Istres : examen de vue, conseil & essayage, adaptation lentilles." />
      {/* Header band */}
      <section className="relative" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${accent})` }}>
        <div className="max-w-4xl mx-auto px-6 py-12 text-center">
          <span className="inline-flex items-center gap-2 text-white/85 text-[12px] font-semibold uppercase tracking-[0.25em]">
            <CalendarCheck size={16} weight="fill" /> Prise de rendez-vous
          </span>
          <h1 className="text-white text-[30px] md:text-[42px] font-semibold mt-2" style={{ fontFamily: "'Poppins', sans-serif" }}>Réservez en ligne</h1>
          {config?.hours_label && <p className="text-white/75 mt-2 text-[14px]">{config.hours_label}</p>}
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Steps */}
        <div className="flex items-center justify-center gap-3 md:gap-6 mb-8">
          <Step n={1} label="Prestation" /><CaretRight size={14} className="text-slate-300" />
          <Step n={2} label="Date & heure" /><CaretRight size={14} className="text-slate-300" />
          <Step n={3} label="Coordonnées" />
        </div>

        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 md:p-8">
          {/* STEP 1: service + practitioner */}
          {step === 1 && (
            <div data-testid="rdv-step-1">
              <h2 className="text-[18px] font-semibold text-slate-900 mb-4">Quelle prestation souhaitez-vous&nbsp;?</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {(config?.services || []).map(s => (
                  <button key={s.id} onClick={() => setService(s)}
                    className="text-left p-4 rounded-2xl border-2 transition-all"
                    style={{ borderColor: service?.id === s.id ? accent : '#e5e7eb', backgroundColor: service?.id === s.id ? accent + '0D' : '#fff' }}
                    data-testid={`rdv-service-${s.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-900 text-[15px]">{s.name}</span>
                      <span className="text-[11px] text-slate-400 flex items-center gap-1"><Clock size={12} /> {s.duration} min</span>
                    </div>
                    <p className="text-[13px] text-slate-500 mt-1">{s.desc}</p>
                  </button>
                ))}
              </div>

              <h2 className="text-[18px] font-semibold text-slate-900 mb-4 mt-8">Avec qui&nbsp;? <span className="text-[13px] font-normal text-slate-400">(optionnel)</span></h2>
              <div className="flex flex-wrap gap-2">
                {(config?.practitioners || []).map(p => (
                  <button key={p.id} onClick={() => setPractitioner(p.id)}
                    className="px-4 py-2.5 rounded-full text-[13.5px] font-medium border-2 transition-all flex items-center gap-2"
                    style={{ borderColor: practitioner === p.id ? accent : '#e5e7eb', color: practitioner === p.id ? accent : '#475569' }}
                    data-testid={`rdv-prac-${p.id}`}>
                    <User size={15} weight="fill" /> {p.name}
                  </button>
                ))}
              </div>

              <div className="flex justify-end mt-8">
                <button disabled={!service} onClick={() => setStep(2)}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white font-semibold disabled:opacity-40 transition-transform hover:scale-105"
                  style={{ backgroundColor: accent }} data-testid="rdv-next-1">
                  Continuer <ArrowRight size={17} weight="bold" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: date + slot */}
          {step === 2 && (
            <div data-testid="rdv-step-2">
              <h2 className="text-[18px] font-semibold text-slate-900 mb-4">Choisissez une date</h2>
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {days.map((d, i) => {
                  const sel = selDate && isoDate(d) === isoDate(selDate);
                  return (
                    <button key={i} onClick={() => pickDate(d)}
                      className="shrink-0 w-[64px] py-2.5 rounded-2xl border-2 text-center transition-all"
                      style={{ borderColor: sel ? accent : '#e5e7eb', backgroundColor: sel ? accent : '#fff', color: sel ? '#fff' : '#0f172a' }}
                      data-testid={`rdv-date-${i}`}>
                      <div className="text-[11px] uppercase opacity-70">{fmtDay(d)}</div>
                      <div className="text-[20px] font-semibold leading-none my-0.5">{fmtNum(d)}</div>
                      <div className="text-[11px] opacity-70">{fmtMon(d)}</div>
                    </button>
                  );
                })}
              </div>

              {selDate && (
                <>
                  <h2 className="text-[18px] font-semibold text-slate-900 mb-3 mt-7">Créneaux disponibles</h2>
                  {loadingSlots ? (
                    <div className="py-8 flex justify-center"><div className="w-7 h-7 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: accent }} /></div>
                  ) : slots.filter(s => s.available).length === 0 ? (
                    <p className="text-slate-400 text-[14px] py-4">Aucun créneau disponible ce jour. Choisissez une autre date.</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {slots.map(s => (
                        <button key={s.time} disabled={!s.available} onClick={() => setSelTime(s.time)}
                          className="py-2.5 rounded-xl text-[14px] font-medium border-2 transition-all disabled:opacity-30 disabled:line-through"
                          style={{ borderColor: selTime === s.time ? accent : '#e5e7eb', backgroundColor: selTime === s.time ? accent : '#fff', color: selTime === s.time ? '#fff' : '#0f172a' }}
                          data-testid={`rdv-slot-${s.time}`}>
                          {s.time}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div className="flex justify-between mt-8">
                <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-slate-600 hover:bg-slate-50">
                  <ArrowLeft size={16} weight="bold" /> Retour
                </button>
                <button disabled={!selDate || !selTime} onClick={() => setStep(3)}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white font-semibold disabled:opacity-40 transition-transform hover:scale-105"
                  style={{ backgroundColor: accent }} data-testid="rdv-next-2">
                  Continuer <ArrowRight size={17} weight="bold" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: contact */}
          {step === 3 && (
            <div data-testid="rdv-step-3">
              <h2 className="text-[18px] font-semibold text-slate-900 mb-1">Vos coordonnées</h2>
              <p className="text-[13.5px] text-slate-500 mb-5">
                {service?.name} · {selDate && new Date(isoDate(selDate)).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à {selTime}
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[12px] font-medium text-slate-500 mb-1 block">Nom complet *</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    className="w-full h-11 px-3.5 rounded-xl border border-slate-200 outline-none focus:border-slate-400 text-[14px]" data-testid="rdv-name" />
                </div>
                <div>
                  <label className="text-[12px] font-medium text-slate-500 mb-1 block">Téléphone *</label>
                  <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                    className="w-full h-11 px-3.5 rounded-xl border border-slate-200 outline-none focus:border-slate-400 text-[14px]" data-testid="rdv-phone" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[12px] font-medium text-slate-500 mb-1 block">Email</label>
                  <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                    className="w-full h-11 px-3.5 rounded-xl border border-slate-200 outline-none focus:border-slate-400 text-[14px]" data-testid="rdv-email" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[12px] font-medium text-slate-500 mb-1 block">Précisions (optionnel)</label>
                  <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-slate-400 text-[14px]" data-testid="rdv-notes" />
                </div>
              </div>
              {error && <p className="text-rose-600 text-[13px] mt-3" data-testid="rdv-error">{error}</p>}
              <div className="flex justify-between mt-8">
                <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-slate-600 hover:bg-slate-50">
                  <ArrowLeft size={16} weight="bold" /> Retour
                </button>
                <button disabled={submitting} onClick={submit}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white font-semibold disabled:opacity-50 transition-transform hover:scale-105"
                  style={{ backgroundColor: accent }} data-testid="rdv-submit">
                  {submitting ? 'Confirmation…' : 'Confirmer le rendez-vous'} <CheckCircle size={17} weight="bold" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RendezVousPage;
